import type { Question, QuotaGroup } from '@api/surveys/utilities/types';
import { RepromptIcon, Button, GlobeIcon, ModalV2 as Modal, Select } from '@utilities';
import {
  getSelectNumericValue,
  getSelectStringValue,
} from '@utilities/components/Select/utilities/helpers';

import styles from './_index.module.scss';
import { buildPreviewUrl, getDropdownOptions, type PreviewSelection } from './utilities';

interface ModalPreviewProps {
  onClose: () => void;
  onQuestionChange: (questionId: number) => void;
  onQuotaGroupChange: (quotaGroupId: string | number) => void;
  onRestart: () => void;
  previewSelection: PreviewSelection;
  previewSessionId: string | null;
  questions: Question[];
  quotaGroups: QuotaGroup[];
}

const ModalPreview = ({
  onClose,
  onQuestionChange,
  onQuotaGroupChange,
  onRestart,
  previewSelection = {},
  previewSessionId,
  questions,
  quotaGroups,
}: ModalPreviewProps) => {
  const iframeUrl = buildPreviewUrl({
    previewSelection,
    previewSessionId,
  });
  const showQuotaGroupDropdown = quotaGroups.length > 1;
  const quotaGroupOptions = getDropdownOptions(quotaGroups);
  const questionOptions = getDropdownOptions(questions);

  return (
    <Modal
      className={styles['modal-preview']}
      hasCloseIcon
      isActive={true}
      onClose={onClose}
      size="fullscreen"
    >
      <div className={styles['modal-preview-header']}>
        <div className={styles['modal-preview-header-toolbar']}>
          <div className={styles['modal-preview-header-dropdowns']}>
            {showQuotaGroupDropdown && (
              <Select
                className={styles['modal-preview-quota-group-dropdown']}
                onChange={(option) => {
                  const value = getSelectStringValue(option);
                  if (value) onQuotaGroupChange(value);
                }}
                options={quotaGroupOptions}
                value={quotaGroupOptions.find(
                  (option) => String(option.value) === String(previewSelection?.quotaGroupId)
                )}
              />
            )}
            {showQuotaGroupDropdown && (
              <span className={styles['modal-preview-vertical-divider']} />
            )}
            <Select
              className={styles['modal-preview-question-dropdown']}
              onChange={(option) => {
                const value = getSelectNumericValue(option);
                if (value) onQuestionChange(value);
              }}
              options={questionOptions}
              value={questionOptions.find(
                (option) => option.value === previewSelection?.questionId
              )}
            />
          </div>
          <Button
            className={styles['modal-preview-restart-button']}
            icon={<RepromptIcon />}
            onClick={onRestart}
            text="Restart"
          />
        </div>
        <Button
          className={styles['modal-preview-public-link-button']}
          icon={<GlobeIcon />}
          text="Public Link"
          variant="secondary"
        />
      </div>
      <div className={styles['modal-preview-content']}>
        <div className={styles['modal-preview-viewport']}>
          <div className={styles['modal-preview-mobile-device-frame']}>
            <iframe
              allow="clipboard-write"
              className={styles['modal-preview-iframe']}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              sandbox="allow-popups allow-scripts allow-same-origin"
              src={iframeUrl}
              title="Instant Survey Preview"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalPreview;
